import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import DataStatusBadge from "./DataStatusBadge";
import { scoreColor, COMPONENT_ICON } from "../../lib/sales";

export default function EvidenceDrawer({ component, onClose }) {
  return (
    <AnimatePresence>
      {component && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose} className="fixed inset-0 z-50 bg-black/45 backdrop-blur-sm" data-testid="evidence-backdrop" />
          <motion.aside initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="fixed right-0 top-0 z-50 h-full w-full max-w-md overflow-y-auto shadow-2xl"
            style={{ background: "#F7F8F6" }} data-testid="evidence-drawer">
            <Body component={component} onClose={onClose} />
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}

function Body({ component, onClose }) {
  const c = scoreColor(component.score);
  const Icon = COMPONENT_ICON[component.key];
  const ev = component.evidence || {};
  return (
    <div>
      <div className="sticky top-0 z-10 flex items-center justify-between border-b px-6 py-4" style={{ borderColor: "#E7E8E4", background: "rgba(247,248,246,.92)", backdropFilter: "blur(8px)" }}>
        <div className="flex items-center gap-2.5">
          {Icon && <span className="grid place-items-center w-9 h-9 rounded-lg" style={{ background: c.bg }}><Icon size={17} strokeWidth={1.75} style={{ color: c.fg }} /></span>}
          <div>
            <div className="label-caps">Why Growth scored this</div>
            <div className="text-sm font-bold text-ink">{component.name}</div>
          </div>
        </div>
        <button onClick={onClose} data-testid="evidence-close" className="grid place-items-center w-9 h-9 rounded-lg text-ink-2 hover:bg-black/5 focus-ring"><X size={18} /></button>
      </div>

      <div className="p-6 space-y-6">
        <div className="flex items-center gap-4">
          <div className="grid place-items-center rounded-2xl w-20 h-20 font-extrabold text-2xl tabular-nums" style={{ background: c.bg, color: c.fg }}>{component.score}</div>
          <div>
            <div className="text-lg font-bold text-ink">{component.band}</div>
            <div className="text-sm text-ink-2">{component.question}</div>
            <div className="text-xs text-ink-3 mt-1">Weight in score: {component.weight}%</div>
          </div>
        </div>

        <div>
          <div className="label-caps mb-2">The evidence</div>
          <div className="card divide-y" style={{ borderColor: "#E7E8E4" }}>
            {(ev.lines || []).map((l, i) => (
              <div key={i} className="px-4 py-3 text-sm text-ink flex items-start gap-2" data-testid={`evidence-line-${i}`}>
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: c.fg }} />
                <span>{l}</span>
              </div>
            ))}
          </div>
        </div>

        {ev.benchmark && (
          <div className="rounded-xl p-4" style={{ background: "var(--grad-green-soft)", border: "1px solid rgba(147,192,32,.3)" }}>
            <div className="label-caps mb-1" style={{ color: "#5F8213" }}>Growth benchmark</div>
            <div className="text-sm font-semibold text-ink">{ev.benchmark}</div>
          </div>
        )}

        <div className="flex items-center justify-between pt-2 border-t" style={{ borderColor: "#E7E8E4" }}>
          <span className="text-xs text-ink-3">Data status</span>
          <DataStatusBadge status={ev.status} />
        </div>
      </div>
    </div>
  );
}
